/**
 * API Utilities
 *
 * Common utilities for onboarding API calls.
 * Provides standardized error handling and response parsing.
 */
import { OnboardingApiError, type MMTErrorDetails } from '@contracts';

/**
 * Base response structure that all onboarding APIs return
 * Note: errorDetails is typed with undefined to satisfy exactOptionalPropertyTypes
 */
interface BaseApiResponse {
  responseStatus: 'SUCCESS' | 'FAILED';
  errorDetails?: MMTErrorDetails | undefined;
}

/**
 * Options for making an API call
 */
interface ApiCallOptions {
  /** The URL to fetch */
  url: string;
  /** HTTP method */
  method: 'GET' | 'POST';
  /** Request headers */
  headers?: HeadersInit;
  /** Request body (for POST requests) */
  body?: unknown;
}

/**
 * Makes an API call and returns the raw JSON response
 *
 * This function:
 * 1. Makes the fetch request
 * 2. Returns the JSON response along with the Response object
 *
 * @param options - API call configuration
 * @returns Object containing the raw data and fetch Response
 */
export async function fetchApi(
  options: ApiCallOptions,
): Promise<{ data: unknown; response: Response }> {
  const { url, method, headers, body } = options;

  const fetchOptions: RequestInit = {
    method,
  };

  // Only add headers if provided (satisfies exactOptionalPropertyTypes)
  if (headers !== undefined) {
    fetchOptions.headers = headers;
  }

  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);
  const data: unknown = await response.json();

  return { data, response };
}

/**
 * Validates the API response and throws OnboardingApiError if failed
 *
 * @param parsedResponse - The parsed response object (must have responseStatus and optional errorDetails)
 * @param response - The fetch Response object for HTTP status info
 * @throws OnboardingApiError on HTTP errors or FAILED responseStatus
 */
export function validateApiResponse(parsedResponse: BaseApiResponse, response: Response): void {
  if (!response.ok || parsedResponse.responseStatus === 'FAILED') {
    throw new OnboardingApiError(
      parsedResponse.errorDetails?.errorDescription ??
        `HTTP ${response.status}: ${response.statusText}`,
      parsedResponse.errorDetails,
      parsedResponse.errorDetails?.statusCode ?? response.status,
    );
  }
}

/**
 * Creates standard headers for authenticated API calls
 * Wrapper around buildHeaders from api/utils for convenience
 */
export { buildHeaders, buildUrl } from '@/api/utils';
